import React, {
  useCallback,
} from 'react';
import {
  Box,
  Button,
  Heading,
  ListItem,
  Flex,
  Text,
  UnorderedList,
  useBreakpointValue,
  useDisclosure,
  ModalOverlay,
  ModalContent,
  Spinner,
  Modal,
} from '@chakra-ui/react';
import { BusinessModel } from '../../interfaces/BusinessModel.interface';
import analytics from '../../services/analytics/analytics';
import { INTAKE_ACCOUNT_LINKING_STARTED } from '../../services/analytics/events';
import rollbar from '../../services/intake/rollbar';
import ErrorAlert from './ErrorAlert';
import LinkingModal from './LinkingModal';
import usePrepareSocialLinking from './usePrepareSocialLinking';
import ExternalLink from './ExternalLink';

const AyrshareView = ({ business, onComplete }: {
  business: BusinessModel,
  onComplete: () => void
}): JSX.Element => {
  const isSmallScreen = useBreakpointValue({ base: true, sm: false });
  const { isOpen, onOpen, onClose } = useDisclosure();

  const [prepareSocialLinking, loading, error] = usePrepareSocialLinking();

  const handleClose = useCallback(
    async (accepted?: boolean) => {
      onClose();

      if (!accepted) return;

      analytics()?.track(INTAKE_ACCOUNT_LINKING_STARTED);
      const redirect = new URL(window.location.href);
      // Set new "back" url...
      redirect.searchParams.set('step', 'complete');
      window.history.pushState({}, '', redirect.href);

      const ayrshareUrl = await prepareSocialLinking(
        business,
        redirect.href,
      );

      if (!ayrshareUrl) return;

      rollbar.info('Redirect To Ayrshare', ayrshareUrl, business);
      window.location.href = ayrshareUrl;
    },
    [onClose],
  );

  // @ts-ignore
  return (
    <Box maxW="4xl">
      <Flex direction="column" gap="1em" w="100%">
        <Heading fontSize="2xl" mb={6}>Payment Method Successful!</Heading>
        <Heading fontSize="2xl" mb={6}>Connect Your Social Accounts To Complete Setup</Heading>

        <Text>Link one or both of your social accounts so we can start posting on your behalf.</Text>

        <Text>What you need:</Text>
        <UnorderedList>
          <ListItem>Login credentials to your social accounts</ListItem>
          <ListItem>
            &quot;Full Control&quot; access over the Facebook Page or Instagram Account you&apos;re connecting
          </ListItem>
        </UnorderedList>

        <Text>
          If you do not have this access today, select &quot;I&apos;ll connect later&quot;.{' '}
          <b>
            If you experience any issues during the linking process, exit the webpage and notify us at{' '}
            <ExternalLink href="mailto:support@getintheloop.ca">support@getintheloop.ca</ExternalLink>{' '}
            so your customer success manager can help.
          </b>
        </Text>

        <Flex gap="1em" pt="1em" flexWrap="wrap">
          <Button
            onClick={onOpen}
            w={isSmallScreen ? '100%' : undefined}
            colorScheme="blue"
          >
            Link Social Accounts
          </Button>
          <Button
            onClick={onComplete}
            w={isSmallScreen ? '100%' : undefined}
          >
            I'll Connect Later
          </Button>
        </Flex>

        <Text fontSize="sm" color="gray.600">
          See <ExternalLink href="https://support.getintheloop.ca/knowledge/social-ai">
          full details </ExternalLink> of requirements for
          Facebook and Instagram accounts.
        </Text>

        <ErrorAlert error={error} />
      </Flex>

      <LinkingModal isOpen={isOpen} onClose={handleClose} />

      <Modal isOpen={loading} isCentered size="xl" scrollBehavior="inside" variant="ghost" onClose={() => { }}>
        <ModalOverlay />
        <ModalContent alignItems="center" color="white">
          <Spinner />
          <Text textAlign="center">We're preparing to link your socials, this should only take a minute.</Text>
          <Text textAlign="center">Please do not refresh...</Text>
        </ModalContent>
      </Modal>
    </Box>
  );
};

export default AyrshareView;
